import { Button, Card, List, Tag } from "antd";
import CustomTable from "@component/CustomTable";
import { ColumnsType } from "antd/es/table";
import {
  AlarmInfoDetailItem,
  AlarmInfoItem,
  BatteryListItem,
  MarkerListItem,
  SensorStatusItem,
  TemperatureHumidity,
} from "@interface/dashboard/Dashboard";
import KakaoMap from "@component/kakaoMap/KakaoMap";
import BatteryStatus from "@page/chart/BatteryStatus";
import TemperatureHumidityChartConfig from "@page/chart/TemperatureHumidityChartConfig";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  getAlarmData,
  getBatteryListData,
  getMakerListData,
  getSensorListData,
  getWeatherData,
} from "@api/DashboardApi";

import IconMenu from "@icon/IconMenu";
import { HttpStatusCode } from "axios";
import IconHumidity from "@icon/IconHumidity";
import IconBattery from "@icon/IconBattery";
import IconGps from "@icon/IconGps";
import IconProtractor from "@icon/IconProtractor";
import IconShock from "@icon/IconShock";
import IconSound from "@icon/IconSound";
import IconEmergency from "@icon/IconEmergency";
import IconHome from "@icon/IconHome";

const Dashboard = ({ onChange }: { onChange: (flag: boolean) => void }) => {
  const navigate = useNavigate();
  const [temperatureHumidityData, setTemperatureHumidityData] =
    useState<TemperatureHumidity>({
      humidityList: [],
      temperatureList: [],
    });

  const [sensorList, setSensorList] = useState<SensorStatusItem[]>([]);
  const [lastDataTime, setLastDataTime] = useState<string | null>(null);
  const [markerList, setMarkerList] = useState<MarkerListItem[]>([]);
  const [mapRerander, setMapRerander] = useState<boolean>(false);
  const [batteryList, setBatteryList] = useState<BatteryListItem[]>([]);
  const [alarmInfoList, setAlarmInfoList] = useState<AlarmInfoItem[]>([]);
  const [alarmInfoDetailList, setAlarmInfoDetailList] = useState<
    AlarmInfoDetailItem[]
  >([]);

  const [abnormalNtnlPntNoList, setAbnormalNtnlPntNoList] = useState<string[]>(
    []
  );

  const columns: ColumnsType<SensorStatusItem> = [
    {
      title: "센서종류",
      key: "sensorType",
      dataIndex: "sensorType",
      align: "left",
      render: (value, record) => {
        //우선 어떻게 넘어올지 몰라 type으로 받았는데. 추후 데이터가 쌓이면 Icon 도 테이블에서 관리하는편이 적절하다 판단 됩니다.(센서 종류를 테이블로 관리)
        let rsForm: JSX.Element = <></>;
        if (value === "thermometerHalf") {
          rsForm = (
            <div>
              <IconHumidity />
              {record.sensorName}
            </div>
          );
        }

        if (value === "battery") {
          rsForm = (
            <div>
              <IconBattery />
              {record.sensorName}
            </div>
          );
        }

        if (value === "gps") {
          rsForm = (
            <div>
              <IconGps />
              {record.sensorName}
            </div>
          );
        }

        if (value === "slope") {
          rsForm = (
            <div>
              <IconProtractor />
              {record.sensorName}
            </div>
          );
        }

        if (value === "shock") {
          rsForm = (
            <div>
              <IconShock />
              {record.sensorName}
            </div>
          );
        }
        if (value === "sound") {
          rsForm = (
            <div>
              <IconSound />
              {record.sensorName}
            </div>
          );
        }
        if (value === "switch") {
          rsForm = (
            <div>
              <IconEmergency />
              {record.sensorName}
            </div>
          );
        }
        return rsForm;
      },
    },
    {
      title: "전체",
      key: "totalCnt",
      dataIndex: "totalCnt",
      align: "center",
      width: "16%",
    },
    {
      title: "정상",
      key: "normalCnt",
      dataIndex: "normalCnt",
      align: "center",
      width: "16%",
    },
    {
      title: "비정상",
      key: "abnormalCnt",
      dataIndex: "abnormalCnt",
      align: "center",
      width: "16%",
    },
  ];

  const getWeatherDataList = async () => {
    getWeatherData().then((res) => {
      if (!res.item) {
        return;
      }

      setTemperatureHumidityData(res.item);
    });
  };

  const getSensorDataList = async () => {
    getSensorListData().then((res) => {
      if (!res.item) {
        return;
      }
      setSensorList(res.item.sensorStatusList ?? []);
      setLastDataTime(res.item.lastDataTime);
    });
  };

  const getMarkerDataList = async () => {
    getMakerListData().then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setMarkerList(res.item);
      }
    });
  };

  const getBatteryDataList = async () => {
    getBatteryListData().then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setBatteryList(res.item);
      }
    });
  };

  const getAlarm = async () => {
    getAlarmData().then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setAlarmInfoList(
          res.item.dashboardAlarmList.filter(
            (item) => item.sensorType !== "switch"
          )
        );
        setAlarmInfoDetailList(res.item.dashboardAlarmDetailList);
      }
    });
  };

  useEffect(() => {
    getWeatherDataList();
    getSensorDataList();
    getMarkerDataList();
    getBatteryDataList();
    getAlarm();
  }, []);

  useEffect(() => {
    console.log(abnormalNtnlPntNoList);
    const markerListSetting = markerList.map((item) => {
      const copyItem = { ...item };
      if (abnormalNtnlPntNoList.includes(item.ntnlPntNo)) {
        copyItem.isFocus = true;
      } else {
        copyItem.isFocus = false;
      }
      return copyItem;
    });
    setMarkerList(markerListSetting);
  }, [abnormalNtnlPntNoList]);

  useEffect(() => {
    setMapRerander(true);
  }, [markerList]);

  return (
    <div className="dashboard-wrap">
      <div>
        <Card
          title="센서현황"
          extra={
            <span className="last-data-time">
              최종 수신:{" "}
              {lastDataTime
                ? dayjs(lastDataTime).format("YYYY-MM-DD HH:mm:ss")
                : "-"}
            </span>
          }
        >
          <div className="box-dashboard type-01-01">
            <CustomTable
              rowNoFlag={true}
              columns={columns}
              dataSource={sensorList}
              onRow={(record) => {
                return {
                  onClick: () => {
                    setAbnormalNtnlPntNoList(record.abnormalNtnlPntNo);
                    setMapRerander(false);
                    // setMarkerList([]);
                  }, // click row
                  onDoubleClick: () => {
                    navigate("/dashboard-detail", {
                      state: { sensorType: record.sensorType },
                    });
                  }, // double click row
                };
              }}
            ></CustomTable>
          </div>
        </Card>
        <Card title="권역별 배터리 현황">
          <div className="box-dashboard type-01-02">
            <BatteryStatus data={batteryList}></BatteryStatus>
          </div>
        </Card>
        <Card title="권역별 온/습도 현황">
          <div className="box-dashboard type-01-03">
            <TemperatureHumidityChartConfig
              lineData={temperatureHumidityData.humidityList}
              barData={temperatureHumidityData.temperatureList}
            />
          </div>
        </Card>
      </div>
      <div style={{ position: "relative" }}>
        <div></div>
        <button
          type="button"
          className="icon-home"
          onClick={() => {
            setAbnormalNtnlPntNoList([]);
            setMapRerander(false);
          }}
        >
          <IconHome />
          북한산
        </button>
        <Card style={{ height: "100%" }} id="map">
          {/*마커 툴팁 text는 string으로 받습니다.*/}
          {/*현재는 경계 좌표(initMaxLocation)를 의정부 시청과 , 북한산 뾰족한 부분을 경계로 삼았습니다. 우선 경계이며 추후 중심이동 동작이 너무 빈번 하다면 영역을 넓히는 방법이 있습니다.*/}
          {markerList.length > 0 && mapRerander ? (
            <KakaoMap
              isInitLocationFlag={true}
              initMaxLocation={{
                leftLatitude: 37.58105,
                leftHardness: 126.868398,
                rightLatitude: 37.754996,
                rightHardness: 127.127741,
              }}
              maxLevel={8}
              currentLevel={8}
              mapWidth={"100%"}
              mapHeight={"500px"}
              initLocation={{ latitude: 37.66711, hardness: 126.991238 }}
              markerList={markerList}
            />
          ) : null}
        </Card>
      </div>
      <div>
        <Card title="알람현황">
          <div className="box-dashboard type-03-01">
            {alarmInfoList
              .reduce((acc: JSX.Element[][], item, index) => {
                const button = (
                  <div key={`alarm_${index}`}>
                    <Button>
                      <div className="txt-tit">
                        {item.sensorType === "thermometerHalf" && (
                          <>
                            {" "}
                            <span className="icon">{<IconHumidity />}</span>
                            온습도{" "}
                          </>
                        )}
                        {item.sensorType === "battery" && (
                          <>
                            {" "}
                            <span className="icon">{<IconBattery />}</span>
                            배터리{" "}
                          </>
                        )}
                        {item.sensorType === "gps" && (
                          <>
                            {" "}
                            <span className="icon">{<IconGps />}</span>GNSS{" "}
                          </>
                        )}
                        {item.sensorType === "sound" && (
                          <>
                            {" "}
                            <span className="icon">
                              {<IconSound />}
                            </span>소리{" "}
                          </>
                        )}
                        {item.sensorType === "slope" && (
                          <>
                            {" "}
                            <span className="icon">{<IconProtractor />}</span>
                            기울기{" "}
                          </>
                        )}
                        {item.sensorType === "shock" && (
                          <>
                            {" "}
                            <span className="icon">
                              {<IconShock />}
                            </span>충격{" "}
                          </>
                        )}
                        {/* {item.sensorType === 'switch' && (
                                                <> <span className="icon">{<IconEmergency/>}</span>비상스위치 </>
                                            )} */}
                      </div>
                      <div>
                        <span>D</span>
                        <span className={"icon-circle type-orange"}>
                          {item.dayCnt}
                        </span>
                        <span>M</span>
                        <span className={"icon-circle type-green"}>
                          {item.monthCnt}
                        </span>
                        <span>Y</span>
                        <span className={"icon-circle type-blue"}>
                          {item.yearCnt}
                        </span>
                      </div>
                    </Button>
                  </div>
                );

                if (index % 2 === 0) {
                  acc.push([button]);
                } else {
                  acc[acc.length - 1].push(button);
                }

                return acc;
              }, [])
              .map((buttonGroup, groupIndex) => (
                <div key={`group_${groupIndex}`}>{buttonGroup}</div>
              ))}
          </div>
        </Card>

        <Card title="알람상세">
          <div className="box-dashboard type-03-02">
            <List
              dataSource={alarmInfoDetailList}
              renderItem={(item, index) => (
                <List.Item key={`alarmDetail${index}`}>
                  <List.Item.Meta
                    avatar={<IconMenu />}
                    title={
                      <div className="title-list">
                        {item.sensorType === "temp" ||
                        item.sensorType === "hum" ? (
                          <Tag
                            color="#22C55E"
                            icon={<IconHumidity />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "battery" ? (
                          <Tag
                            color="#22C55E"
                            icon={<IconBattery />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "gps" ? (
                          <Tag
                            color="#8E33FF"
                            icon={<IconGps />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "slope" ? (
                          <Tag
                            color="#454F5B"
                            icon={<IconProtractor />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "switch" ? (
                          <Tag
                            color="#FF4646"
                            icon={<IconEmergency />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "sound" ? (
                          <Tag
                            color="#F07E26"
                            icon={<IconSound />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : item.sensorType === "shock" ? (
                          <Tag
                            color="#F07E26"
                            icon={<IconShock />}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                              setMapRerander(false);
                            }}
                          >
                            {item.sensorName}
                          </Tag>
                        ) : null}
                        <span
                          className="num"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                            setMapRerander(false);
                          }}
                        >
                          {item.ntnlPntNo}
                        </span>
                        <span
                          className="num bg"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setAbnormalNtnlPntNoList([item.ntnlPntNo]);
                            setMapRerander(false);
                          }}
                        >
                          {item.lctnSignNo}
                        </span>
                      </div>
                    }
                    description={item.content}
                  />
                </List.Item>
              )}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
